import './top-progress.css';

const progressBarId = 'ips-frontfw-top-progress';
const activeThreshold = 90;
const startWidth = 90;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export class TopProgressController {
  private element: HTMLDivElement | null = null;

  private progress = 0;

  private activeRequests = 0;

  private hideTimer: number | null = null;

  private fadeTimer: number | null = null;

  private hasResizeListener = false;

  start(): void {
    const element = this.ensureElement();
    this.clearHideTimer();
    this.clearFadeTimer();

    element.classList.remove('ips-frontfw-top-progress--error');
    element.classList.remove('ips-frontfw-top-progress--completing');

    if (this.activeRequests === 0) {
      this.progress = 0;
      element.style.width = '0%';
      element.style.opacity = '1';
      element.style.display = 'block';
      // Force reflow so the start animation always runs from 0% to 90%.
      void element.offsetWidth;
      this.setProgressInternal(startWidth, true);
    }

    this.activeRequests += 1;
  }

  setProgress(value: number): void {
    if (this.activeRequests < 1) {
      return;
    }

    this.setProgressInternal(clamp(value, 0, activeThreshold));
  }

  done(): void {
    this.finish(false);
  }

  fail(): void {
    this.finish(true);
  }

  reset(): void {
    this.activeRequests = 0;
    this.clearHideTimer();
    this.clearFadeTimer();

    const element = this.ensureElement();
    element.classList.remove('ips-frontfw-top-progress--error');
    element.classList.remove('ips-frontfw-top-progress--completing');
    element.style.display = 'none';
    element.style.width = '0%';
    element.style.opacity = '1';
    this.progress = 0;
  }

  private finish(isError: boolean): void {
    if (this.activeRequests < 1) {
      return;
    }

    this.activeRequests -= 1;

    if (this.activeRequests > 0) {
      return;
    }

    const element = this.ensureElement();
    this.clearHideTimer();
    this.clearFadeTimer();

    if (isError) {
      element.classList.add('ips-frontfw-top-progress--error');
    }

    element.classList.add('ips-frontfw-top-progress--completing');
    this.setProgressInternal(100, true);

    this.fadeTimer = window.setTimeout(() => {
      if (this.activeRequests > 0) {
        return;
      }

      element.style.opacity = '0';
    }, 210);

    this.hideTimer = window.setTimeout(() => {
      if (this.activeRequests > 0) {
        return;
      }

      element.style.display = 'none';
      element.style.width = '0%';
      element.style.opacity = '1';
      element.classList.remove('ips-frontfw-top-progress--error');
      element.classList.remove('ips-frontfw-top-progress--completing');
      this.progress = 0;
    }, isError ? 760 : 540);
  }

  private setProgressInternal(value: number, force = false): void {
    const element = this.ensureElement();
    const nextValue = force ? clamp(value, 0, 100) : Math.max(this.progress, clamp(value, 0, 100));
    this.progress = nextValue;
    element.style.width = `${nextValue}%`;
    element.setAttribute('aria-valuenow', String(Math.round(nextValue)));
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private clearFadeTimer(): void {
    if (this.fadeTimer !== null) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  private ensureElement(): HTMLDivElement {
    if (this.element && document.body.contains(this.element)) {
      this.syncTopOffset();
      return this.element;
    }

    const existing = document.getElementById(progressBarId) as HTMLDivElement | null;
    const element = existing || document.createElement('div');

    if (!existing) {
      element.id = progressBarId;
      element.setAttribute('role', 'progressbar');
      element.setAttribute('aria-valuemin', '0');
      element.setAttribute('aria-valuemax', '100');
      element.setAttribute('aria-valuenow', '0');
      element.style.display = 'none';
    }

    const adminBar = document.getElementById('wpadminbar');
    if (!existing) {
      if (adminBar?.parentElement) {
        adminBar.parentElement.insertBefore(element, adminBar.nextSibling);
      } else {
        document.body.prepend(element);
      }
    }

    this.element = element;
    this.syncTopOffset();

    if (!this.hasResizeListener) {
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.hasResizeListener = true;
    }

    return element;
  }

  private handleResize = (): void => {
    this.syncTopOffset();
  };

  private syncTopOffset(): void {
    if (!this.element) {
      return;
    }

    const adminBar = document.getElementById('wpadminbar');
    const topOffset = adminBar ? Math.max(0, Math.round(adminBar.getBoundingClientRect().height)) : 0;
    this.element.style.top = `${topOffset}px`;
  }
}
